import { Component, computed, ElementRef, inject, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { CommonButtonRowWidget, CommonNumberFieldWidget, CommonTextFieldWidget, CommonUrlFieldWidget, UtilityService } from "@eforall/common";
import { DialogService } from "@eforall/dashboard";
import { AccVentureUpdateValues, DropoutUpsertValues } from "@interfaces";
import { AppService, DomainDataService } from "../../../../../services";
import { ReadonlyAccessWidget } from "../../../readonly-access/readonly-access.widget";
import { DropoutDialog } from "../../dropout";
import { DropoutDialogData } from "../../dropout/dropout.dialog";
import { AccVentureDialogDataService } from "../acc-venture-dialog-data.service";
import { DetailsPartForm } from "./details-part.form";

@Component({
	selector: 'acc-venture-details-part',
	standalone: true,
	imports: [
		CommonUrlFieldWidget,
		CommonButtonRowWidget,
		ReadonlyAccessWidget,
		CommonTextFieldWidget,
		CommonNumberFieldWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './details.part.html'
})
export class AccVentureDetailsPart {

	private readonly util = inject(UtilityService);
	private readonly app = inject(AppService);
	private readonly dialogService = inject(DialogService);
	private readonly domainDataService = inject(DomainDataService);
	public readonly dialogDataService = inject(AccVentureDialogDataService);

	public readonly form = new DetailsPartForm();

	@ViewChild('saveBtn')
	set formSubmitButton(button: ElementRef<HTMLButtonElement>) {
		if (button) {
			this.form.form.setSubmitButton(button, async (values: AccVentureUpdateValues, changed: string[]) => {

				if (changed.length === 0) return;

				const changedValues: AccVentureUpdateValues = {
					ventureName: values.ventureName,
					websiteURL: values.websiteURL,
					workbookDriveURL: values.workbookDriveURL,
					workbookScore: values.workbookScore,
				};

				await this.dialogDataService.updateVentureDetails(changedValues);
			});
		}
	}

	public ventureDetails = computed(() => {
		const venture = this.dialogDataService.dialogData();
		if (!venture) return undefined;

		const industriesMap = this.domainDataService.data().industriesMap;

		return {
			...venture,
			industry: industriesMap[venture.industryId]?.name ?? 'N/A',
			entrepreneurs: venture.accEntrepreneurs.map(e => e.fullName).join(', '),
			courseCompletionPercentage: Math.max(...venture.accEntrepreneurs.map(e => e.completionPercentage), 0),
			formattedCreatedDate: this.util.date.formatUTC(venture.createdUTC, 'MMM D, YYYY', 'H:MM AM EST', 'en-US'),
			formattedUpdatedDate: this.util.date.formatUTC(venture.updatedUTC, 'MMM D, YYYY', 'H:MM AM EST', 'en-US'),
			dropout: venture.dropout ? {
				...venture.dropout,
				droppedOutByStaff: this.app.getStaffNameById(venture.dropout.createdByStaffId),
				dropoutReason: this.domainDataService.data().dropoutReasonsMap[venture.dropout.dropoutReasonId]?.name,
			} : undefined
		};
	});

	openURL(url: string) {
		window.open(url, '_blank');
	}

	async openDropoutDialog() {
		const data = this.dialogDataService.dialogData();
		if (!data) return;

		const action = await this.dialogService.showCustom<DropoutDialog, DropoutDialogData>(DropoutDialog, { data: { dropoutTargetType: 'Venture', dropoutId: data.dropout?.dropoutId } }, 650, 500);

		const result = action?.callbackResult as DropoutUpsertValues | undefined;
		if (result) {
			await this.dialogDataService.addOrUpdateDropout(result);
		}
	}
}